import {createContext, ReactNode, useContext, useEffect, useMemo, useRef} from "react";
import {EventEmitter2} from "./util/EventEmitter2";

// A registry of keyboard hints ("Press Delete to remove") contributed by the
// menubar's contextual tabs. Each tab registers its hints on mount and removes
// them on unmount, so the set always reflects what's actionable right now. The
// StatusBar under the canvas reads the set and surfaces one at a time.
type StatusHintContextValue = {
    // register a hint; returns an unregister function to call on unmount.
    register: (text: string) => () => void;
    // the live set of hints, keyed by an internal registration id.
    hints: EventEmitter2<Map<number, string>>;
};

const StatusHintContext = createContext<StatusHintContextValue | null>(null);

export function StatusHintProvider(props: {children: ReactNode}) {
    const hints = useMemo(() => new EventEmitter2<Map<number, string>>(new Map(), "status.hints"), []);
    const nextId = useRef(0);

    const value = useMemo<StatusHintContextValue>(() => ({
        hints,
        register(text: string) {
            const id = nextId.current++;
            const added = new Map(hints.value);
            added.set(id, text);
            hints.emit(added);

            return () => {
                const removed = new Map(hints.value);
                removed.delete(id);
                hints.emit(removed);
            };
        },
    }), [hints]);

    return <StatusHintContext.Provider value={value}>{props.children}</StatusHintContext.Provider>;
}

// register a status-bar hint for as long as the calling component is mounted.
// Pass a falsy value to register nothing (for conditionally-available actions).
export function useStatusHint(text: string | null | undefined) {
    const ctx = useContext(StatusHintContext);

    useEffect(() => {
        if(!ctx || !text) return;
        return ctx.register(text);
    }, [ctx, text]);
}

export function useStatusHints() {
    return useContext(StatusHintContext);
}
